import * as React from 'react';

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/componentes/ui/table';
import { cn } from '@/lib/utils';

/* TabelaCenarios — atual contra sugerido, indicador por indicador.
 *
 * A coluna de comparação é uma CÉLULA de mint atravessando a tabela inteira,
 * com raio nas pontas: ela não é "mais uma coluna", é o cenário alternativo
 * inteiro, destacado como um bloco.
 *
 * E ela é OPCIONAL de propósito. Quando a margem já supera a meta não existe
 * o que sugerir, e a coluna some em vez de mostrar um traço: o preço sugerido
 * é o PISO da meta, jamais um alvo a perseguir para baixo. Omitir
 * `colunaSugerido` é o jeito de dizer isso na tela. */

export type TomDelta = 'positivo' | 'negativo' | 'neutro';

export type LinhaCenario = {
  metrica: string;
  /** Uma linha dizendo em que unidade o indicador está. */
  nota?: string | undefined;
  /** Já formatado. Normalmente um `<Valor>`. */
  atual: React.ReactNode;
  /** O mesmo indicador no cenário sugerido. */
  sugerido?: React.ReactNode;
  /** A diferença entre os dois, com sinal explícito. */
  delta?: React.ReactNode;
  tomDelta?: TomDelta | undefined;
};

export type TabelaCenariosProps = {
  linhas: readonly LinhaCenario[];
  colunaMetrica?: string | undefined;
  colunaAtual?: string | undefined;
  /** Título da coluna de comparação. Sem ele, a coluna não existe. */
  colunaSugerido?: string | undefined;
  /** Uma linha sob a tabela, para o que o número sozinho não conta. */
  rodape?: React.ReactNode;
  /**
   * O que a tabela mostra, para quem chega por leitor de tela e não vê o
   * cabeçalho da seção. Fica fora da tela, não fora da árvore acessível.
   */
  legenda: string;
  className?: string | undefined;
};

const TOM_DELTA: Record<TomDelta, string> = {
  positivo: 'text-positivo',
  negativo: 'text-negativo',
  neutro: 'text-tinta-2',
};

export function TabelaCenarios({
  linhas,
  colunaMetrica = 'Indicador',
  colunaAtual = 'Atual',
  colunaSugerido,
  rodape,
  legenda,
  className,
}: TabelaCenariosProps) {
  const comparando = colunaSugerido !== undefined;

  return (
    <div className={className}>
      <Table>
        <TableCaption className="sr-only">{legenda}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">{colunaMetrica}</TableHead>
            <TableHead scope="col" className="text-right">
              {colunaAtual}
            </TableHead>
            {comparando ? (
              <TableHead scope="col" className="text-right">
                {colunaSugerido}
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.map((linha) => (
            <TableRow key={linha.metrica}>
              <TableHead scope="row" className="text-tinta font-sans font-medium whitespace-normal">
                {linha.metrica}
                {linha.nota ? (
                  <small className="text-tinta-2 mt-px block text-nota font-normal">{linha.nota}</small>
                ) : null}
              </TableHead>

              <TableCell className="numero text-tinta-2 text-right font-semibold">
                {linha.atual}
              </TableCell>

              {comparando ? (
                <TableCell data-realce="mint" className="numero text-tinta text-right font-semibold">
                  {linha.sugerido}
                  {linha.delta ? (
                    <span
                      className={cn(
                        'mt-0.5 block text-nota font-semibold',
                        TOM_DELTA[linha.tomDelta ?? 'positivo'],
                      )}
                    >
                      {linha.delta}
                    </span>
                  ) : null}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {rodape ? <p className="apoio-nota mt-4">{rodape}</p> : null}
    </div>
  );
}
